import React, { useState, useEffect, useRef, useCallback } from 'react';

/* ── Typewriter ── */
function Typewriter({ text, speed = 25, onComplete }) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setDisplayed('');
    setDone(false);
    let i = 0;
    intervalRef.current = setInterval(() => {
      i++;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(intervalRef.current);
        setDone(true);
        if (onComplete) onComplete();
      }
    }, speed);
    return () => clearInterval(intervalRef.current);
  }, [text, speed, onComplete]);

  const skip = useCallback(() => {
    if (!done) {
      clearInterval(intervalRef.current);
      setDisplayed(text);
      setDone(true);
      if (onComplete) onComplete();
    }
  }, [done, text, onComplete]);

  return (
    <span className="typewriter" onClick={skip}>
      {displayed}
      {!done && <span className="typewriter-cursor">&#x2588;</span>}
    </span>
  );
}

/* ── Main DialogueView ── */
function DialogueView({ round, trust, onChoice, sceneDescription, npc }) {
  const [phase, setPhase] = useState('npc_speaking');
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [npcDone, setNpcDone] = useState(false);
  const [responseDone, setResponseDone] = useState(false);

  const npcText = round.getNpcText ? round.getNpcText(trust) : round.npcText;

  // Reset when round changes
  useEffect(() => {
    setPhase('npc_speaking');
    setSelectedChoice(null);
    setNpcDone(false);
    setResponseDone(false);
  }, [round.id]);

  const handleNpcComplete = useCallback(() => {
    setNpcDone(true);
    setPhase('choosing');
  }, []);

  const handleResponseComplete = useCallback(() => {
    setResponseDone(true);
  }, []);

  const handleChoiceClick = (choice) => {
    setSelectedChoice(choice);
    setPhase('response');
    setResponseDone(false);
  };

  const getResponse = (choice) => {
    if (choice.getResponse) return choice.getResponse(trust + choice.trustDelta);
    return choice.response;
  };

  const handleContinue = () => {
    if (selectedChoice) onChoice(selectedChoice);
  };

  const choiceTypeLabel = (type) => {
    switch (type) {
      case 'aggressive': return 'DIRECT';
      case 'academic': return 'FORMAL';
      case 'smart': return 'STREET SMART';
      default: return type.toUpperCase();
    }
  };

  return (
    <div className="dialogue-view">
      {/* Scene description (only round 1) */}
      {sceneDescription && phase === 'npc_speaking' && (
        <div className="scene-description">
          <span className="scene-icon">&#x25C8;</span>
          {sceneDescription}
        </div>
      )}

      {/* NPC Area */}
      <div className="npc-section">
        <div className="npc-avatar-column">
          <div className="npc-hologram">
            <div className="hologram-ring hologram-ring-outer" />
            <div className="hologram-ring hologram-ring-inner" />
            <div className="hologram-core">
              <span className="hologram-brackets">{'{ }'}</span>
            </div>
            <div className="hologram-scanline" />
          </div>
          <div className="npc-nameplate">
            <div className="npc-name">{npc ? npc.name : 'AXIOM-7'}</div>
            <div className="npc-role">{npc ? npc.title : 'Gatekeeper Protocol'}</div>
          </div>
        </div>

        <div className="npc-dialogue-column">
          {/* NPC initial text */}
          {phase === 'npc_speaking' && (
            <div className="npc-bubble">
              <Typewriter text={npcText} speed={22} onComplete={handleNpcComplete} />
            </div>
          )}
          {phase !== 'npc_speaking' && (
            <div className="npc-bubble npc-bubble-complete">
              {npcText}
            </div>
          )}

          {/* After choice: show player choice + NPC response */}
          {(phase === 'response') && selectedChoice && (
            <>
              <div className="player-bubble">
                <span className="player-tag">YOU</span>
                {selectedChoice.text}
              </div>
              <div className={`npc-bubble npc-response npc-response-${selectedChoice.type}`}>
                <Typewriter
                  text={getResponse(selectedChoice)}
                  speed={18}
                  onComplete={handleResponseComplete}
                />
              </div>

              {responseDone && (
                <div className={`feedback-box feedback-${selectedChoice.type}`}>
                  <span className="feedback-indicator">
                    {selectedChoice.type === 'smart' ? '\u2713' : selectedChoice.type === 'aggressive' ? '\u2717' : '\u2014'}
                  </span>
                  <span className="feedback-text">{selectedChoice.feedback}</span>
                </div>
              )}

              {responseDone && (
                <button className="btn-primary btn-continue" onClick={handleContinue}>
                  CONTINUE
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Choices */}
      {phase === 'choosing' && (
        <div className="choices-panel">
          <div className="choices-header">CHOOSE YOUR APPROACH</div>
          <div className="choices-grid">
            {round.choices.map((choice) => (
              <button
                key={choice.id}
                className={`choice-card choice-${choice.type}`}
                onClick={() => handleChoiceClick(choice)}
              >
                <span className={`choice-type-badge choice-type-${choice.type}`}>
                  {choiceTypeLabel(choice.type)}
                </span>
                <span className="choice-label">{choice.label}</span>
                <span className="choice-preview">{choice.text}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DialogueView;
